class WeightCalculator extends HTMLElement {
  minRecommendedWeight = 5;
  formula = 'standard';

  // GROESSENUMRECHNUNG UEBER DIE FLAECHE
  //
  // Referenz ist die 135 x 200 cm mit 2,70 m2. Deren Formel bleibt unveraendert.
  // Jede groessere Decke bekommt das Deckengewicht im Verhaeltnis ihrer Flaeche,
  // weil sich dasselbe Gewicht dort auf mehr Stoff verteilt und spuerbar
  // weniger drueckt. 155 x 220 cm = 3,41 m2, also Faktor 3,41 / 2,70 = 1,263
  // (+26,3 %).
  //
  // Warum die Masse aus dem Optionswert gelesen und nicht fest eingetragen
  // werden: Kommt eine dritte Groesse dazu, rechnet der Rechner sofort richtig,
  // ohne dass hier jemand eine Zahl nachtragen muss.
  //
  // Abgerundet wird bewusst (Math.floor). Aufgerundet kaeme bei 6 und 7 kg
  // Basisempfehlung eine Stufe mehr heraus — das widerspricht der müüd-Formel,
  // die im Zweifel leichter empfiehlt, weil „zu schwer" der haeufigste
  // Retourengrund ist. Mit Abrunden ergibt der Faktor ueber die kaufbare
  // Spanne 5–9 kg genau eine Stufe Aufschlag; die veroeffentlichten
  // Bereichstabellen bleiben dadurch unveraendert gueltig.
  //
  // Datengrundlage fuer den Aufschlag (Retouren 12 Monate, 2.782 Bestellungen,
  // 08.08.2026): 155er-Quote 14,1 % gegenueber 11,1 %, und innerhalb der 155er
  // schneidet ausgerechnet die schwerere 7-kg-Variante am besten ab (10,3 %),
  // die leichte 5-kg-Variante am schlechtesten (14,0 %). Statistisch noch nicht
  // signifikant (z ~ 1,6) — im November 2026 mit retourenquote.py nachmessen.
  referenzBreite = 135;
  referenzLaenge = 200;

  connectedCallback() {
    this.input = this.querySelector('.weight-calculator__input');
    this.rangeOutput = this.querySelector('[data-range-value]');
    this.recommendedOutput = this.querySelector('[data-recommended-value]');

    if (!this.input) return;

    this.formula = this.dataset.formula || 'standard';
    this.minRecommendedWeight = Number(this.dataset.minRecommendedWeight) || (this.formula === 'kids' ? 2 : 5);
    this.handleRangeInput = this.handleRangeInput.bind(this);
    this.handleRangeCommit = this.handleRangeCommit.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.lastCommittedRecommended = Number(this.recommendedOutput?.getAttribute('data-recommended-value'));
    this.preserveBodyWeightOnNextUpdate = null;
    this.variantWeights = this.getVariantWeights();
    this.letzterFaktor = this.flaechenFaktor();
    // MESSUNG (17.10.2026): hat der Kunde den Regler selbst bewegt?
    // Ohne diese Unterscheidung waere die Empfehlung wertlos — beim
    // Seitenaufbau zeigt der Rechner naemlich das Gewicht der gewaehlten
    // Variante zurueckgerechnet an, Abweichung scheinbar 0.
    this.benutzt = false;

    this.init();
    this.messwerteSchreiben();
    this.winkenVorbereiten();
  }

  // Aufmerksamkeits-Wackler (17.10.2026, Weisung Hendrik: mehr Leute
  // sollen den Rechner benutzen): wackelt EINMAL, wenn der unbenutzte
  // Rechner zu 60 % im Bild steht. Ein Beobachter mit engem Ziel und
  // disconnect() beim ersten Treffer — Hausregel-konform, kein Dauer-JS.
  winkenVorbereiten() {
    if (this.benutzt || !('IntersectionObserver' in window)) return;
    const beobachter = new IntersectionObserver((eintraege) => {
      if (!eintraege.some((e) => e.isIntersecting)) return;
      beobachter.disconnect();
      if (!this.benutzt) this.classList.add('weight-calculator--winken');
    }, { threshold: 0.6 });
    beobachter.observe(this);
  }

  init() {
    this.input.addEventListener('input', this.handleRangeInput);
    this.input.addEventListener('change', this.handleRangeCommit);

    // Auf dem product-info-Container lauschen, nicht auf variant-selects:
    // Beim Variantenwechsel tauscht product-info.js die Variantenauswahl im DOM
    // aus. Ein Listener direkt darauf waere danach weg.
    this.produktInfo = this.closest('product-info');
    this.produktInfo?.addEventListener('change', this.handleVariantChange);
  }

  disconnectedCallback() {
    this.input?.removeEventListener('input', this.handleRangeInput);
    this.input?.removeEventListener('change', this.handleRangeCommit);
    this.produktInfo?.removeEventListener('change', this.handleVariantChange);
    clearTimeout(this.nachfassTimer);
  }

  /**
   * Reagiert auf jede Aenderung in der Variantenauswahl.
   *
   * Warum zusaetzlich zu update(): update() wird von product-info.js gerufen
   * und setzt voraus, dass im nachgeladenen HTML ein data-recommended-value
   * steckt. Faellt das aus, bliebe die Empfehlung stehen — genau der Fall, in
   * dem der Kunde die Groesse wechselt, ohne den Regler anzufassen. Dieser Weg
   * haengt an nichts davon.
   *
   * Das Nachfassen per Timer faengt den Fall ab, dass die Variantenauswahl erst
   * nach dem Ereignis neu gerendert wird und der Optionswert im Moment des
   * Ereignisses noch die alte Groesse nennt.
   */
  handleVariantChange() {
    this.groessenwechselPruefen();
    clearTimeout(this.nachfassTimer);
    this.nachfassTimer = setTimeout(() => {
      this.groessenwechselPruefen();
      this.messwerteSchreiben();
    }, 300);
    // Auch bei reinem Gewichtswechsel (kein Groessenwechsel) neu schreiben:
    // genau dann weicht der Kunde womoeglich von der Empfehlung ab.
    this.messwerteSchreiben();
  }

  /**
   * Hat sich die Deckengroesse geaendert, bleibt das eingestellte
   * Koerpergewicht stehen — das ist die Angabe des Kunden und darf nicht
   * ueberschrieben werden. Neu gerechnet wird nur die Empfehlung, denn die
   * haengt an der Flaeche.
   * Gibt true zurueck, wenn tatsaechlich ein Groessenwechsel vorlag.
   */
  groessenwechselPruefen() {
    const faktor = this.flaechenFaktor();
    if (this.letzterFaktor !== undefined && this.letzterFaktor === faktor) return false;

    this.letzterFaktor = faktor;
    if (!this.input) return false;

    // Koerpergewicht festhalten. Nach dem Groessenwechsel laedt product-info.js
    // die Variante nach und ruft update() — das wuerde den Regler auf den Wert
    // der bereits gewaehlten Gewichtsvariante ziehen und die Eingabe des Kunden
    // ueberschreiben. Ohne diese Zeile springt der Regler z. B. von 82 auf 100.
    this.preserveBodyWeightOnNextUpdate = Number(this.input.value);

    this.applyRecommendedDisplay(this.getRecommendedFromInput());
    this.updateFill();
    return true;
  }

  getVariantSelects() {
    return this.closest('product-info')?.querySelector('variant-selects');
  }

  getVariantWeights() {
    const raw = this.dataset.variantWeights;
    if (!raw) return [];

    try {
      const weights = JSON.parse(raw)
        .map((weight) => Number(weight))
        .filter((weight) => !Number.isNaN(weight) && weight >= this.minRecommendedWeight);

      return [...new Set(weights)].sort((a, b) => a - b);
    } catch {
      return [];
    }
  }

  /**
   * Liest die Masse der gerade gewaehlten Deckengroesse aus der
   * Variantenauswahl, z. B. "155 x 220 cm" -> { breite: 155, laenge: 220 }.
   * Akzeptiert x, × und *. Die Plausibilitaetsgrenze 80–300 cm verhindert,
   * dass Werte anderer Optionen (Gewicht, Farbe) faelschlich als Mass gelesen
   * werden. Findet sich nichts, wird null zurueckgegeben — der Rechner faellt
   * dann auf die Referenzgroesse zurueck und rechnet wie bisher.
   */
  gewaehlteMasse() {
    if (this.formula === 'kids') return null;

    const auswahl = this.getVariantSelects();
    if (!auswahl) return null;

    const felder = auswahl.querySelectorAll('input[type="radio"]:checked, select, [data-option-value].is-active, [aria-checked="true"]');
    for (const feld of felder) {
      const wert = (feld.value ?? feld.getAttribute('data-option-value') ?? feld.textContent ?? '').toString();
      const treffer = wert.match(/(\d{2,3})\s*[x×*]\s*(\d{2,3})/i);
      if (!treffer) continue;

      const breite = Number(treffer[1]);
      const laenge = Number(treffer[2]);
      if (breite >= 80 && breite <= 300 && laenge >= 80 && laenge <= 300) {
        return { breite, laenge };
      }
    }
    return null;
  }

  /**
   * Flaeche der gewaehlten Decke geteilt durch die Flaeche der Referenzgroesse.
   * 135 x 200 -> 1, 155 x 220 -> 1,263. Unbekannte Groesse -> 1.
   */
  flaechenFaktor() {
    const masse = this.gewaehlteMasse();
    const referenz = this.referenzBreite * this.referenzLaenge;
    if (!masse || !referenz) return 1;

    return (masse.breite * masse.laenge) / referenz;
  }

  /**
   * Die müüd-Formel fuer die Referenzgroesse: Koerpergewicht / 10 - 1,
   * abgerundet, ab 0,8 aufgerundet. Bewusst OHNE Untergrenze — die greift erst
   * nach der Flaechenumrechnung, sonst verschluckt sie bei leichten Personen
   * den Groessenaufschlag.
   */
  basisEmpfehlung(bodyWeight) {
    const calculated = Math.round(bodyWeight * 10) / 100 - 1;
    const baseWeight = Math.floor(calculated);
    const decimalWeight = Math.round((calculated - baseWeight) * 10) / 10;

    return decimalWeight >= 0.8 ? baseWeight + 1 : baseWeight;
  }

  getRecommendedWeight(bodyWeight) {
    if (this.formula === 'kids') {
      if (bodyWeight <= 30) return 2;
      if (bodyWeight <= 40) return 3;
      return 4;
    }

    const skaliert = Math.floor(this.basisEmpfehlung(bodyWeight) * this.flaechenFaktor());

    return Math.max(this.minRecommendedWeight, skaliert);
  }

  /**
   * Obergrenze, falls die Variantenliste leer ankommt. Ohne diese Absicherung
   * schlaegt der Rechner Gewichte vor, die es nicht zu kaufen gibt (z. B. 10 kg).
   */
  maxRecommendedFallback() {
    return this.formula === 'kids' ? 4 : 9;
  }

  getValidatedRecommended(calculated) {
    const minimumCalculated = Math.max(this.minRecommendedWeight, calculated);
    if (!this.variantWeights.length) {
      return Math.min(minimumCalculated, this.maxRecommendedFallback());
    }

    let result = this.variantWeights[0];
    for (const option of this.variantWeights) {
      if (option <= minimumCalculated) {
        result = option;
      } else {
        break;
      }
    }

    return result;
  }

  formatWeightKg(weight) {
    const rounded = Math.round(weight * 10) / 10;
    return Number.isInteger(rounded) ? `${rounded}` : rounded.toFixed(1);
  }

  formatBodyWeightKg(weight) {
    const max = Number(this.input.max);
    const formattedWeight = this.formatWeightKg(weight);

    return !Number.isNaN(max) && weight >= max ? `${formattedWeight} kg+` : `${formattedWeight} kg`;
  }

  basisAusEmpfehlung(gewicht) {
    const faktor = this.flaechenFaktor();
    if (!(faktor > 0)) return gewicht;

    return Math.ceil(gewicht / faktor - 1e-9);
  }

  getBodyWeightFromRecommended(selectedWeight) {
    const weight = Number(selectedWeight);
    if (Number.isNaN(weight) || weight <= 0) return null;
    const recommendedWeight = Math.max(this.minRecommendedWeight, weight);

    const min = Number(this.input.min);
    const max = Number(this.input.max);
    let bodyWeight;

    if (this.formula === 'kids') {
      if (recommendedWeight <= 2) {
        bodyWeight = 30;
      } else if (recommendedWeight <= 3) {
        bodyWeight = 40;
      } else {
        bodyWeight = 50;
      }
    } else {
      // Umkehrung der Formel — die Flaechenumrechnung muss hier wieder raus,
      // sonst springt der Regler beim Wechsel der Deckengroesse falsch.
      //
      // Achtung: Math.floor laesst sich nicht durch einfaches Teilen umkehren.
      // Damit floor(basis * faktor) das gewaehlte Deckengewicht ergibt, muss
      // die Basis mindestens gewicht / faktor betragen — also AUFrunden.
      // Mit einer simplen Division laege der Regler bei 7 kg auf der 155er
      // eine Stufe zu niedrig. Das Epsilon faengt Rundungsrauschen ab, damit
      // bei Faktor 1 aus 6 nicht 7 wird.
      bodyWeight = (this.basisAusEmpfehlung(recommendedWeight) + 1) * 10;
    }

    if (bodyWeight < min) bodyWeight = min;
    if (bodyWeight > max) bodyWeight = max;

    return { weight: recommendedWeight, bodyWeight };
  }

  /**
   * Tauscht den Beschreibungstext passend zur empfohlenen Gewichtsstufe aus.
   * Die Texte kommen als JSON aus snippets/weight-calculator.liquid. Ist fuer
   * eine Stufe nichts gepflegt, bleibt der allgemeine Text stehen.
   */
  beschreibungAktualisieren(gewicht) {
    const feld = this.querySelector('.weight-calculator__description');
    if (!feld) return;

    if (this.beschreibungen === undefined) {
      try {
        this.beschreibungen = JSON.parse(feld.dataset.beschreibungen || '{}');
      } catch {
        this.beschreibungen = {};
      }
      this.standardtext = feld.dataset.standardtext || feld.textContent.trim();
    }

    const text = this.beschreibungen[String(gewicht)] || this.standardtext;
    if (text && feld.textContent.trim() !== text) {
      feld.textContent = text;
    }
  }

  applyRecommendedDisplay(recommended, options = {}) {
    if (!this.recommendedOutput) return;

    const { commit = true } = options;

    this.recommendedOutput.setAttribute('data-recommended-value', String(recommended));
    this.recommendedOutput.textContent = `${this.formatWeightKg(recommended)} kg`;
    // Sobald zum ersten Mal eine Empfehlung feststeht, ist der unbestimmte
    // Startzustand vorbei: Stufenbeschreibung wieder einblenden (16.08.2026,
    // siehe Kopfkommentar in snippets/weight-calculator.liquid).
    this.classList.remove('weight-calculator--unbestimmt');
    this.beschreibungAktualisieren(recommended);
    if (commit) {
      this.lastCommittedRecommended = recommended;
      this.messwerteSchreiben();
    }
  }

  /**
   * MESSUNG RECHNER-NUTZUNG (17.10.2026, Weisung Hendrik).
   *
   * Schreibt drei versteckte Positions-Eigenschaften in das
   * In-den-Warenkorb-Formular, damit spaeter pro Bestellung auswertbar ist,
   * ob der Kunde der Empfehlung gefolgt ist und ob Abweichler oefter
   * retournieren (Auswertung: muud-shopify/retoure-oder-umtausch.py):
   *
   *   _rechner      ja | nein  (Regler selbst bewegt?)
   *   _empfehlung   empfohlene Gewichtsstufe in kg, z. B. "7"
   *   _abweichung   gewaehltes minus empfohlenes Gewicht, z. B. "+1", "0"
   *
   * Bewusst wird NICHT das Koerpergewicht gespeichert — die Empfehlung
   * genuegt fuer die Auswertung und ist keine Gesundheitsangabe.
   * Der Unterstrich-Prafix haelt die Werte aus Warenkorb, Kasse und
   * Bestellbestaetigung heraus (Theme filtert '_', geprueft 17.10.2026 in
   * sections/main-cart.liquid und sections/cart-drawer.liquid).
   *
   * KEIN Dauer-JS: Aufrufe haengen ausschliesslich an bestehenden
   * Ereignissen (Seitenaufbau, Reglerbewegung, Variantenwechsel) —
   * kein Beobachter, kein Intervall. Mehrfachaufrufe sind idempotent.
   */
  messwerteSchreiben() {
    const wurzel = this.closest('product-info') || document;
    const formulare = wurzel.querySelectorAll('form[action*="/cart/add"]');
    if (!formulare.length) return;

    const empfehlung = Number(this.recommendedOutput?.getAttribute('data-recommended-value'));
    const gewaehlt = this.gewaehltesGewicht();
    const mitWerten = this.benutzt && empfehlung > 0;

    for (const formular of formulare) {
      this.feldSetzen(formular, '_rechner', this.benutzt ? 'ja' : 'nein');
      this.feldSetzen(formular, '_empfehlung', mitWerten ? String(empfehlung) : null);
      if (mitWerten && gewaehlt !== null) {
        const differenz = gewaehlt - empfehlung;
        this.feldSetzen(formular, '_abweichung', (differenz > 0 ? '+' : '') + differenz);
      } else {
        this.feldSetzen(formular, '_abweichung', null);
      }
    }

    this.schwerHinweis(mitWerten ? empfehlung : null, gewaehlt);
  }

  /**
   * SANFTE LENKUNG WEG VOM ZU SCHWEREN GEWICHT (17.10.2026, Weisung Hendrik).
   * Datenlage: 135/9 kg hat 22,7 %, 135/8 kg 20,3 % Erstattungen (Amazon,
   * kohortenkorrekt); "zu schwer" ist Rueckgabegrund Nr. 1 (41 %).
   * Zeigt NUR dann einen Hinweis unter der Gewichtsauswahl, wenn der Kunde
   * den Rechner benutzt hat UND ein schwereres Gewicht als empfohlen waehlt.
   * Ein Klick wechselt auf die Empfehlung — kein Blocker, kein Modal, der
   * Kaufen-Button bleibt unberuehrt (Lehre aus dem Gewichts-Check-Popup,
   * das 57 % Bestellungen kostete). Haengt an denselben Ereignissen wie
   * messwerteSchreiben — kein Dauer-JS.
   */
  schwerHinweis(empfehlung, gewaehlt) {
    const alt = document.getElementById('muud-schwer-hinweis');
    const zeigen = empfehlung !== null && gewaehlt !== null && gewaehlt > empfehlung;
    if (!zeigen) {
      alt?.remove();
      return;
    }

    const anker = this.gewichtsFieldset();
    if (!anker) return;

    let kasten = alt;
    if (!kasten) {
      kasten = document.createElement('div');
      kasten.id = 'muud-schwer-hinweis';
      kasten.innerHTML = '<span class="muud-schwer-hinweis__text"></span>'
        + '<button type="button" class="muud-schwer-hinweis__knopf"></button>';
      kasten.querySelector('button').addEventListener('click', () => {
        const ziel = Number(kasten.dataset.empfehlung);
        const auswahl = this.getVariantSelects();
        if (ziel && auswahl?.selectOptionValueByWeight) auswahl.selectOptionValueByWeight(ziel);
      });
    }
    kasten.dataset.empfehlung = String(empfehlung);
    kasten.querySelector('.muud-schwer-hinweis__text').textContent =
      'Du hast ' + this.formatWeightKg(gewaehlt) + ' kg gewählt – für dich empfohlen sind '
      + this.formatWeightKg(empfehlung) + ' kg. Zu schwer ist Rückgabegrund Nr. 1.';
    kasten.querySelector('button').textContent = 'Auf ' + this.formatWeightKg(empfehlung) + ' kg wechseln';
    if (kasten.parentNode !== anker.parentNode || kasten.previousElementSibling !== anker) {
      anker.insertAdjacentElement('afterend', kasten);
    }
  }

  /** Das Fieldset der Gewichtsauswahl (enthaelt die kg-Radios). */
  gewichtsFieldset() {
    const auswahl = this.getVariantSelects();
    if (!auswahl) return null;
    for (const radio of auswahl.querySelectorAll('input[type="radio"]')) {
      if (/^\s*\d{1,2}(?:[.,]\d)?\s*kg\s*$/i.test(radio.value)) {
        return radio.closest('fieldset, .product-form__input');
      }
    }
    return null;
  }

  /** Legt ein verstecktes properties[...]-Feld an bzw. aktualisiert es.
   *  wert null entfernt das Feld wieder (leere Werte sollen gar nicht erst
   *  in der Bestellung auftauchen). */
  feldSetzen(formular, name, wert) {
    const voll = `properties[${name}]`;
    let feld = formular.querySelector(`input[name="${CSS.escape(voll)}"]`);
    if (wert === null) {
      feld?.remove();
      return;
    }
    if (!feld) {
      feld = document.createElement('input');
      feld.type = 'hidden';
      feld.name = voll;
      formular.appendChild(feld);
    }
    feld.value = wert;
  }

  /**
   * Liest die gerade gewaehlte GEWICHTS-Variante (z. B. "7 kg" -> 7).
   * Gleiche Quellen wie gewaehlteMasse(); die Masse-Werte ("155 x 220 cm")
   * fallen durch, weil vor dem "kg" dort kein alleinstehender Wert 1–15
   * steht. Findet sich nichts, null — dann wird keine Abweichung notiert.
   */
  gewaehltesGewicht() {
    const auswahl = this.getVariantSelects();
    if (!auswahl) return null;

    const felder = auswahl.querySelectorAll('input[type="radio"]:checked, select, [data-option-value].is-active, [aria-checked="true"]');
    for (const feld of felder) {
      const wert = (feld.value ?? feld.getAttribute('data-option-value') ?? feld.textContent ?? '').toString();
      if (/[x×*]/.test(wert)) continue;
      const treffer = wert.match(/(\d{1,2}(?:[.,]\d)?)\s*kg\b/i);
      if (!treffer) continue;

      const gewicht = Number(treffer[1].replace(',', '.'));
      if (gewicht >= 1 && gewicht <= 15) return gewicht;
    }
    return null;
  }

  getRecommendedFromInput() {
    const value = Number(this.input.value);
    const calculated = this.getRecommendedWeight(value);

    return this.getValidatedRecommended(calculated);
  }

  handleRangeInput() {
    this.benutzt = true;
    const recommended = this.getRecommendedFromInput();

    this.updateFill();
    this.applyRecommendedDisplay(recommended, { commit: false });
  }

  handleRangeCommit() {
    this.benutzt = true;
    const value = Number(this.input.value);
    const recommended = this.getRecommendedFromInput();

    if (!Number.isNaN(this.lastCommittedRecommended) && this.lastCommittedRecommended === recommended) {
      return;
    }

    const variantSelects = this.getVariantSelects();

    if (variantSelects?.selectOptionValueByWeight) {
      this.preserveBodyWeightOnNextUpdate = value;

      if (variantSelects.selectOptionValueByWeight(recommended)) {
        this.lastCommittedRecommended = recommended;
        return;
      }

      this.preserveBodyWeightOnNextUpdate = null;
    }

    this.applyRecommendedDisplay(recommended);
  }

  update(selectedWeight) {
    const parsed = this.getBodyWeightFromRecommended(selectedWeight);
    if (!parsed) return;

    const { weight, bodyWeight } = parsed;
    const preserveBodyWeight = this.preserveBodyWeightOnNextUpdate;
    this.preserveBodyWeightOnNextUpdate = null;

    const currentWeight = Number(this.recommendedOutput?.getAttribute('data-recommended-value'));

    if (preserveBodyWeight != null && !Number.isNaN(preserveBodyWeight)) {
      // Das eingestellte Koerpergewicht ist die Angabe des Kunden und bleibt
      // stehen. Die Empfehlung wird aus genau diesem Wert neu gerechnet — nicht
      // aus der uebergebenen Gewichtsvariante. Sonst zeigt der Rechner nach
      // einem Groessenwechsel weiter die Zahl der alten Groesse.
      this.input.value = preserveBodyWeight;
      this.letzterFaktor = this.flaechenFaktor();
      this.applyRecommendedDisplay(this.getRecommendedFromInput());
      this.updateFill();
      return;
    }

    // Wechselt nur die Deckengroesse, bleibt das Deckengewicht gleich — der
    // zugehoerige Koerpergewichtswert aendert sich aber. Deshalb darf hier
    // nicht vorzeitig abgebrochen werden, sonst zeigt der Regler den alten Wert.
    // Sicherheitsnetz, falls der Groessenwechsel nicht ueber das change-Ereignis
    // ankam (z. B. programmatisch gesetzte Variante). Koerpergewicht bleibt,
    // nur die Empfehlung wird neu gerechnet.
    if (this.groessenwechselPruefen()) {
      this.preserveBodyWeightOnNextUpdate = null;
      return;
    }

    if (!Number.isNaN(currentWeight) && currentWeight === weight) return;

    this.input.value = bodyWeight;
    this.applyRecommendedDisplay(weight);
    this.updateFill();
  }

  updateFill() {
    const min = Number(this.input.min);
    const max = Number(this.input.max);
    const value = Number(this.input.value);
    const span = max - min;
    const percent = span > 0 ? ((value - min) / span) * 100 : 0;

    this.input.style.setProperty('--fill-percent', `${percent}%`);

    if (this.rangeOutput) {
      this.rangeOutput.textContent = this.formatBodyWeightKg(value);
    }
  }
}

if (!customElements.get('weight-calculator')) {
  customElements.define('weight-calculator', WeightCalculator);
}
